import React, { useState } from "react";

/**
 * A number input you can actually type in.
 *
 * Clamping on every keystroke is the usual trap: a field sitting at 1 can
 * never be emptied, so typing 24 is impossible (you get 1, then 12, then
 * 124 clamped back). This keeps whatever is typed while the field is
 * focused, reports whole numbers up to the parent as they are typed, and
 * only clamps to min/max on blur. An empty field falls back to
 * `fallback`, then `min`, then 0.
 */
const NumberField = ({
  value,
  onChange,
  min,
  max,
  fallback,
  style,
  ...rest
}) => {
  // Null while idle, the raw string while the field is being edited.
  const [draft, setDraft] = useState(null);

  const clamp = (n) => {
    let next = n;
    if (typeof min === "number" && next < min) next = min;
    if (typeof max === "number" && next > max) next = max;
    return next;
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    setDraft(raw);
    const parsed = parseInt(raw, 10);
    // Push the value straight through so previews and help text keep up.
    // Out-of-range typing is tidied on blur, not mid-keystroke.
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  const commit = () => {
    if (draft === null) return;
    const parsed = parseInt(draft, 10);
    const base = Number.isNaN(parsed)
      ? (typeof fallback === "number" ? fallback : (typeof min === "number" ? min : 0))
      : parsed;
    setDraft(null);
    onChange(clamp(base));
  };

  return (
    <input
      {...rest}
      type="number"
      min={min}
      max={max}
      value={draft === null ? (value === undefined || value === null ? "" : value) : draft}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (rest.onKeyDown) rest.onKeyDown(e);
      }}
      style={style}
    />
  );
};

export default NumberField;
